import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Plus, Download, Trash2, ChevronDown, ChevronRight, Camera, FileText, Thermometer, Wind, Droplets } from 'lucide-react';
import { EmptyState } from '@/components/ui/empty-state';
import { cn, formatDateOnly } from '@/lib/utils';
import { todayLocalDateString } from '@/lib/localDate';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useSiteVisits,
  useCreateSiteVisit,
  useDeleteSiteVisit,
  useProjectWeather,
  type SiteVisitType,
  type WeatherCondition,
  type WeatherData,
} from '@/hooks/useSiteVisits';
import { api } from '@/lib/api';
import { SiteVisitStatusBadge } from './SiteVisitStatusBadge';
import { SiteVisitDetailPanel } from './SiteVisitDetailPanel';

interface Props {
  projectId: string;
}

const VISIT_TYPE_LABELS: Record<SiteVisitType, string> = {
  site_visit_info: 'Site Visit for Information',
  site_review: 'Site Review',
  progress: 'Progress Review',
  safety: 'Safety Review',
  punch_list: 'Punch List',
  inspection: 'Site Review', // legacy — mapped for backward compat
  other: 'Other',
};

const CONDITIONS_LABELS: Record<WeatherCondition, string> = {
  clear: 'Clear',
  partly_cloudy: 'Partly Cloudy',
  cloudy: 'Cloudy',
  rain: 'Rain',
  heavy_rain: 'Heavy Rain',
  snow: 'Snow',
  fog: 'Fog',
  thunderstorm: 'Thunderstorm',
  windy: 'Windy',
};

function WeatherChips({ data }: { data: WeatherData }) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-1">
      {data.temperature_c !== null && (
        <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded px-2 py-0.5">
          <Thermometer className="w-3 h-3" />
          {data.temperature_c}°C
        </span>
      )}
      {data.conditions && (
        <span className="inline-flex items-center gap-1 text-xs bg-sky-50 text-sky-700 border border-sky-200 rounded px-2 py-0.5">
          {CONDITIONS_LABELS[data.conditions] ?? data.conditions}
        </span>
      )}
      {data.humidity_pct !== null && (
        <span className="inline-flex items-center gap-1 text-xs bg-teal-50 text-teal-700 border border-teal-200 rounded px-2 py-0.5">
          <Droplets className="w-3 h-3" />
          {data.humidity_pct}%
        </span>
      )}
      {data.wind_kph !== null && (
        <span className="inline-flex items-center gap-1 text-xs bg-gray-50 text-gray-600 border border-gray-200 rounded px-2 py-0.5">
          <Wind className="w-3 h-3" />
          {data.wind_kph} km/h
        </span>
      )}
    </div>
  );
}

export function SiteVisitsSection({ projectId }: Props) {
  const { data: visits = [], isLoading } = useSiteVisits(projectId);
  const createVisit = useCreateSiteVisit();
  const deleteVisit = useDeleteSiteVisit();
  const location = useLocation();

  const [isAdding, setIsAdding] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(() => {
    const state = location.state as { expandVisit?: string } | null;
    return state?.expandVisit ?? null;
  });
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // New visit form state
  const [newTitle, setNewTitle] = useState('');
  const [newDate, setNewDate] = useState(() => todayLocalDateString());
  const [newType, setNewType] = useState<SiteVisitType>('site_review');
  const [newVisitedBy, setNewVisitedBy] = useState('');
  const [visitedByTouched, setVisitedByTouched] = useState(false);
  const [newWeatherNotes, setNewWeatherNotes] = useState('');
  const [newGeneralConditions, setNewGeneralConditions] = useState('');
  const [weatherOverride, setWeatherOverride] = useState<WeatherData | null>(null);

  const visitedByError = visitedByTouched && !newVisitedBy.trim();
  const conductedByErrorId = 'conducted-by-error';

  // Fetch weather when form opens (only when isAdding = true)
  const { data: autoWeather, isFetching: weatherFetching } = useProjectWeather(
    isAdding ? projectId : null
  );

  // Use auto weather unless user has overridden it
  const effectiveWeather = weatherOverride ?? autoWeather ?? null;

  const handleCreate = async () => {
    if (!newTitle.trim() || !newDate || !newVisitedBy.trim()) return;
    const response = await createVisit.mutateAsync({
      projectId,
      visit: {
        title: newTitle.trim(),
        visit_date: newDate,
        visit_type: newType,
        visited_by: newVisitedBy.trim(),
        general_conditions: newGeneralConditions.trim() || undefined,
        weather_notes: newWeatherNotes.trim() || undefined,
        weather_data: effectiveWeather ?? undefined,
      },
    });
    // R1: auto-expand the newly created visit so the detail panel opens immediately
    const newVisit = (response as any)?.site_visit || response;
    if (newVisit?.id) {
      setExpandedId(newVisit.id);
    }
    setNewTitle('');
    setNewVisitedBy('');
    setVisitedByTouched(false);
    setNewWeatherNotes('');
    setNewGeneralConditions('');
    setWeatherOverride(null);
    setIsAdding(false);
  };

  const handleCancelAdd = () => {
    setIsAdding(false);
    setNewTitle('');
    setNewVisitedBy('');
    setVisitedByTouched(false);
    setNewWeatherNotes('');
    setNewGeneralConditions('');
    setWeatherOverride(null);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await deleteVisit.mutateAsync(deleteId);
    setDeleteId(null);
    if (expandedId === deleteId) setExpandedId(null);
  };

  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-4">Loading site visits...</div>;
  }

  return (
    <div className="space-y-3">
      {/* Empty state */}
      {visits.length === 0 && !isAdding && (
        <EmptyState
          icon={Camera}
          title="No site visits logged"
          description="Document field conditions, track issues, generate reports"
          actionLabel="Log Site Visit"
          onAction={() => setIsAdding(true)}
        />
      )}

      {/* Summary bar */}
      {visits.length > 0 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {visits.length} visit{visits.length !== 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => api.siteVisits.exportLog(projectId)}
              title="Export CSV"
            >
              <Download className="w-4 h-4 mr-1" />
              CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => setIsAdding(true)}>
              <Plus className="w-4 h-4 mr-1" />
              Log Visit
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      {visits.length > 0 && (
        <div className="border rounded-lg overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[360px]">
            <thead>
              <tr className="bg-muted/50 text-xs text-muted-foreground">
                <th className="px-3 py-2 text-left w-8"></th>
                <th className="px-3 py-2 text-left w-28">Date</th>
                <th className="px-3 py-2 text-left">Title</th>
                <th className="px-3 py-2 text-left w-32 hidden sm:table-cell">Type</th>
                <th className="px-3 py-2 text-left w-36">Status</th>
                <th className="px-3 py-2 text-left w-36 hidden sm:table-cell">Conducted By</th>
                <th className="px-3 py-2 text-center w-16 hidden sm:table-cell">Photos</th>
                <th className="px-3 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {visits.map((visit) => (
                <>
                  <tr
                    key={visit.id}
                    className={cn(
                      'border-t hover:bg-muted/30 group cursor-pointer',
                      visit.status === 'draft' && 'bg-gray-50/60',
                      visit.status === 'in_progress' && 'bg-blue-50/40',
                      visit.status === 'completed' && 'bg-green-50/40',
                      visit.status === 'submitted' && 'bg-purple-50/40',
                    )}
                    onClick={() => setExpandedId(expandedId === visit.id ? null : visit.id)}
                  >
                    <td className="px-3 py-2">
                      {expandedId === visit.id
                        ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        : <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      }
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {formatDateOnly(visit.visit_date)}
                    </td>
                    <td className="px-3 py-2 font-medium">
                      {visit.title}
                      {visit.checklist_item_count > 0 && (
                        <span className="text-xs text-muted-foreground ml-2">
                          {visit.checklist_item_count} items
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground hidden sm:table-cell">
                      {VISIT_TYPE_LABELS[visit.visit_type] || visit.visit_type}
                    </td>
                    <td className="px-3 py-2">
                      <SiteVisitStatusBadge status={visit.status} />
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[144px] hidden sm:table-cell">
                      {visit.visited_by}
                    </td>
                    <td className="px-3 py-2 text-center text-xs text-muted-foreground hidden sm:table-cell">
                      {visit.photo_count > 0 && (
                        <span className="flex items-center justify-center gap-1">
                          <Camera className="w-3 h-3" />
                          {visit.photo_count}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1 opacity-60 hover:opacity-100 focus-within:opacity-100 transition-opacity">
                        <button
                          className="p-1 hover:bg-muted rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          title="Download PDF Report"
                          onClick={() => api.siteVisits.downloadReport(visit.id)}
                        >
                          <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                        </button>
                        <button
                          className="p-1 hover:bg-destructive/10 hover:text-destructive rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          onClick={() => setDeleteId(visit.id)}
                          title="Delete site visit"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedId === visit.id && (
                    <tr key={`${visit.id}-detail`}>
                      <td colSpan={8} className="p-0">
                        <SiteVisitDetailPanel visitId={visit.id} />
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add form */}
      {isAdding && (
        <div className="border border-dashed border-blue-300 rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Title *</Label>
              <Input
                placeholder="e.g. Framing site review"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                className="h-8 text-sm mt-1"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs">Visit Date *</Label>
              <Input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                className="h-8 text-sm mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={newType} onValueChange={(v) => setNewType(v as SiteVisitType)}>
                <SelectTrigger className="h-8 text-sm mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(VISIT_TYPE_LABELS)
                    .filter(([value]) => value !== 'inspection') // legacy — hide from new visit dropdown
                    .map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs" htmlFor="new-visit-conducted-by">
                Conducted By (email) <span className="text-destructive" aria-hidden="true">*</span>
              </Label>
              <Input
                id="new-visit-conducted-by"
                placeholder="reviewer@example.com"
                value={newVisitedBy}
                onChange={(e) => {
                  setNewVisitedBy(e.target.value);
                  if (visitedByTouched && e.target.value.trim()) setVisitedByTouched(false);
                }}
                onBlur={() => setVisitedByTouched(true)}
                className={cn('h-8 text-sm mt-1', visitedByError && 'border-destructive')}
                aria-invalid={visitedByError ? 'true' : undefined}
                aria-describedby={visitedByError ? conductedByErrorId : undefined}
                aria-required="true"
              />
              {visitedByError && (
                <p
                  id={conductedByErrorId}
                  role="alert"
                  className="text-xs text-destructive mt-1"
                >
                  Conducted By is required.
                </p>
              )}
            </div>

            {/* Weather auto-fill */}
            <div className="col-span-2">
              <Label className="text-xs">Weather</Label>
              {weatherFetching && (
                <p className="text-xs text-muted-foreground mt-1">Fetching weather...</p>
              )}
              {!weatherFetching && effectiveWeather && (
                <div className="mt-1">
                  <WeatherChips data={effectiveWeather} />
                  <button
                    type="button"
                    className="text-xs text-muted-foreground underline mt-1"
                    onClick={() => setWeatherOverride(null)}
                  >
                    {weatherOverride ? 'Reset to auto' : 'Auto-filled'}
                  </button>
                </div>
              )}
              {!weatherFetching && !effectiveWeather && (
                <p className="text-xs text-muted-foreground mt-1">
                  Add a project address to see weather at this location.
                </p>
              )}
              <Textarea
                placeholder="Additional weather notes (optional)"
                value={newWeatherNotes}
                onChange={(e) => setNewWeatherNotes(e.target.value)}
                className="text-sm mt-2 min-h-[48px]"
                rows={2}
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">General Conditions</Label>
              <Textarea
                placeholder="Site conditions, access issues, safety observations, general notes..."
                value={newGeneralConditions}
                onChange={(e) => setNewGeneralConditions(e.target.value)}
                className="text-sm mt-1 min-h-[80px]"
                rows={3}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              className="h-8"
              onClick={handleCreate}
              disabled={!newTitle.trim() || !newDate || !newVisitedBy.trim() || createVisit.isPending}
            >
              Create
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8"
              onClick={handleCancelAdd}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Site Visit</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the site visit, all photos, and all checklist items.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
